export function normalizeBusinessAccessContext(row) {
  if (!row?.businessId) return null;
  const accessType = row.accessType === "owner" ? "owner" : "staff";
  return {
    businessId: row.businessId,
    businessName: row.businessName || "Business",
    accessType,
    ...(accessType === "staff" && row.staffRole ? { staffRole: row.staffRole } : {}),
  };
}

function isMissingStaffAccessSchema(error) {
  const code = String(error?.code || "");
  return code === "42P01" || code === "42703" || code === "PGRST200" || code === "PGRST201";
}

function normalizeBusinessRow(row) {
  if (!row?.id) return null;
  return {
    businessId: row.id,
    businessName: row.business_name || row.name || "Business",
    accessType: "owner",
  };
}

function getJoinedBusiness(row) {
  if (Array.isArray(row?.business)) return row.business[0] || null;
  return row?.business || null;
}

export async function resolveBusinessAccessContexts(supabase, userId) {
  if (!supabase?.from || !userId) {
    return {
      ownedBusinesses: [],
      staffMemberships: [],
      contexts: [],
      hasBusinessAccess: false,
      error: null,
    };
  }

  const [ownerResult, staffResult] = await Promise.all([
    supabase
      .from("businesses")
      .select("id,business_name,owner_user_id")
      .eq("owner_user_id", userId),
    supabase
      .from("business_staff_members")
      .select("id,business_id,user_id,role,status,business:businesses(id,business_name,owner_user_id)")
      .eq("user_id", userId)
      .eq("status", "active")
      .order("created_at", { ascending: true }),
  ]);

  const ownerError = ownerResult?.error || null;
  const staffError = staffResult?.error || null;
  const ownedBusinesses = ownerError
    ? []
    : (ownerResult?.data || []).map(normalizeBusinessRow).filter(Boolean);

  const staffMemberships = staffError && isMissingStaffAccessSchema(staffError)
    ? []
    : (staffResult?.data || [])
        .map((membership) => {
          const business = getJoinedBusiness(membership);
          const context = normalizeBusinessAccessContext({
            businessId: business?.id || membership?.business_id,
            businessName: business?.business_name || "Business",
            accessType: "staff",
            staffRole: membership?.role || null,
          });
          return context
            ? {
                ...context,
                membershipId: membership?.id || null,
                status: membership?.status || "active",
              }
            : null;
        })
        .filter(Boolean);

  const seenBusinessIds = new Set();
  const contexts = [];
  for (const context of [...ownedBusinesses, ...staffMemberships]) {
    if (!context?.businessId || seenBusinessIds.has(context.businessId)) continue;
    seenBusinessIds.add(context.businessId);
    contexts.push(normalizeBusinessAccessContext(context));
  }

  return {
    ownedBusinesses,
    staffMemberships,
    contexts,
    hasBusinessAccess: contexts.length > 0,
    error: ownerError || (staffError && !isMissingStaffAccessSchema(staffError) ? staffError : null),
  };
}
